import type { TravelerDimension, TravelerProfile, TravelerVector } from '@/types/profile'

/**
 * Quiz result shape as actually returned by `getUserQuizResults()`. The
 * `QuizResults` type in `src/types/profile.ts` omits `personality_matrix`
 * because the DB stores it in a JSONB column that isn't part of the public
 * type; we re-declare the slice we care about here for the profile UI.
 */
export interface QuizResultForProfile {
  id?: string
  profile_categories?: string[]
  personality_matrix?: {
    primary_category?: string
    secondary_category?: string
    confidence_score?: number // historically written as either 0–1 or 0–100
    category_scores?: Record<string, number>
    profile_description?: string
  }
  completed_at?: string
  confidence_score?: number
  // VOY-414
  traveler_vector?: TravelerProfile | null
  quiz_version?: string
  metadata?: Record<string, unknown>
}

const DIMENSION_ENDS: Record<TravelerDimension, [string, string]> = {
  adventure_vs_comfort: ['Comfort', 'Adventure'],
  planned_vs_spontaneous: ['Planned', 'Spontaneous'],
  budget_vs_luxury: ['Budget', 'Luxury'],
  solo_vs_social: ['Social', 'Solo'],
  fast_vs_slow: ['Slow', 'Fast'],
  familiar_vs_novel: ['Familiar', 'Novel'],
  urban_vs_nature: ['Nature', 'Urban'],
  food_centrality: ['Casual', 'Foodie'],
}

/**
 * VOY-414: returns the stored traveler profile if the quiz was scored with the
 * v2 vector algorithm. Type-guards the jsonb shape.
 */
export function getTravelerVector(
  quiz: QuizResultForProfile | null | undefined,
): TravelerProfile | null {
  const tv = quiz?.traveler_vector
  if (!tv || typeof tv !== 'object') return null
  if (!tv.vector || typeof tv.vector !== 'object') return null
  return tv
}

/**
 * VOY-414: spectrum entries for the sidebar card. Each dimension becomes a
 * horizontal axis with a marker. Sorted by |value| so the most defining
 * traits rise to the top.
 */
export interface DimensionSpectrumEntry {
  dim: TravelerDimension
  value: number
  /** marker position as [0,1] for CSS % */
  position: number
  leftLabel: string
  rightLabel: string
  confidence: number
}

export function getDimensionSpectrum(
  profile: TravelerProfile | null | undefined,
  limit = 4,
): DimensionSpectrumEntry[] {
  if (!profile?.vector) return []
  const vec = profile.vector as TravelerVector
  const conf = profile.confidence_per_dimension || ({} as TravelerVector)
  const dims = Object.keys(DIMENSION_ENDS) as TravelerDimension[]
  return dims
    .map(dim => {
      const value = Number(vec[dim]) || 0
      const [leftLabel, rightLabel] = DIMENSION_ENDS[dim]
      // food_centrality is [0,1]; map directly. Others are [-1,1] → map to [0,1].
      const position =
        dim === 'food_centrality'
          ? Math.max(0, Math.min(1, value))
          : Math.max(0, Math.min(1, (value + 1) / 2))
      return {
        dim,
        value,
        position,
        leftLabel,
        rightLabel,
        confidence: Number(conf[dim]) || 0,
      }
    })
    // Drop dimensions the quiz had no signal on — showing them misleads the
    // user (e.g. value=0 for an unanswered dim would render as "neutral-max"
    // when it actually means "unknown").
    .filter(entry => entry.confidence > 0)
    .sort((a, b) => {
      // Rank by |distance from neutral| × confidence so strong, well-measured
      // traits surface above weak or lightly-measured ones.
      const magA =
        (a.dim === 'food_centrality' ? Math.abs(a.value - 0.5) * 2 : Math.abs(a.value)) *
        a.confidence
      const magB =
        (b.dim === 'food_centrality' ? Math.abs(b.value - 0.5) * 2 : Math.abs(b.value)) *
        b.confidence
      return magB - magA
    })
    .slice(0, limit)
}

/**
 * VOY-358: the quiz "Profile Confidence" value has historically been written
 * at two scales (0–1 from `submitQuiz` in profile.ts, 0–100 from the mock
 * fallback in travel-quiz.tsx). The reader used to `* 100` unconditionally,
 * producing 8500% for legacy rows. This normalizer handles both — and the
 * writer inconsistency is fixed in Phase 6.
 */
export function normalizeConfidence(raw: unknown): number {
  const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
  const percent = n > 1 ? n : n * 100
  return Math.max(0, Math.min(percent, 100))
}

/**
 * Pick the personality/score dominant entries from the quiz result, clamped
 * and sorted descending. Returns an empty array when there's no quiz data.
 */
export function getTopCategoryScores(
  quiz: QuizResultForProfile | null | undefined,
  limit = 3,
): Array<{ key: string; label: string; percent: number }> {
  const scores = quiz?.personality_matrix?.category_scores
  if (!scores) return []
  return Object.entries(scores)
    .map(([key, value]) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      percent: normalizeConfidence(value),
    }))
    .sort((a, b) => b.percent - a.percent)
    .slice(0, limit)
}

/**
 * Personality pills from `profile_categories` or the primary/secondary fields.
 */
export function getPersonalityTags(
  quiz: QuizResultForProfile | null | undefined,
): string[] {
  if (!quiz) return []
  if (Array.isArray(quiz.profile_categories) && quiz.profile_categories.length > 0) {
    return quiz.profile_categories
  }
  const primary = quiz.personality_matrix?.primary_category
  const secondary = quiz.personality_matrix?.secondary_category
  return [primary, secondary].filter((v): v is string => !!v)
}
